// src/services/jobService.js
import { callApi } from "./api";
import { getUserDocuments } from "./api"; // Import for saveAndMatchResume

// --- Helper Function (Internal) ---
const createDocumentFormData = (
  file,
  title,
  documentType = "resume",
  isPrimary = false
) => {
  const formData = new FormData();
  if (!(file instanceof File)) {
    throw new Error("Invalid file provided for upload");
  }
  formData.append("file", file);
  formData.append("title", title || file.name);
  formData.append("document_type", documentType);
  formData.append("is_primary", isPrimary ? "true" : "false");
  return formData;
};

// --- Internal API Call Functions ---
const submitResumeForMatching = async (email, resumeSource, location) => {
  console.log("[jobService] submitResumeForMatching called...");
  const formData = new FormData();
  formData.append("email", email);

  // Only append resume if it exists and is valid
  if (resumeSource && resumeSource.trim().length >= 50) {
    formData.append("resume", resumeSource);
    console.log("[jobService] Submitting with resume content");
  } else {
    console.log(
      "[jobService] Submitting without resume - backend will use profile data"
    );
    // Backend will automatically handle profile lookup when resume is not provided
  }

  formData.append("location", location);
  return await callApi("POST", `/prompt/job-categories`, formData);
};

// New convenience function for explicit profile-based search
const submitProfileForMatching = async (email, location) => {
  console.log(
    "[jobService] submitProfileForMatching - explicitly using profile data"
  );
  const formData = new FormData();
  formData.append("email", email);
  formData.append("location", location);
  // Intentionally not including resume to trigger profile lookup
  return await callApi("POST", `/prompt/job-categories`, formData);
};

const saveResumeFile = async (file, title = null, isPrimary = false) => {
  console.log(`[jobService] saveResumeFile called: primary=${isPrimary}`);
  const docFormData = createDocumentFormData(file, title, "resume", isPrimary);
  return await callApi("POST", `/profile/resumes/`, docFormData);
};

const uploadResumeFile = async (file) => {
  console.warn(
    "[jobService] uploadResumeFile: Ensure backend has a '/resume/upload' endpoint that returns text."
  );
  const formData = new FormData();
  formData.append("file", file);
  const response = await callApi("POST", `/resume/upload`, formData, {
    headers: { Accept: "text/plain" },
    responseType: "text",
  });
  if (typeof response !== "string") {
    throw new Error(
      "Failed to get text from resume upload. Expected string response."
    );
  }
  return response;
};

const getJobMatchResults = async (taskId) => {
  console.log(
    `[jobService] getJobMatchResults: Fetching status for task ${taskId}`
  );
  return await callApi("GET", `/jobs/${taskId}`);
};

const getSavedResumes = async () => {
  console.log("[jobService] getSavedResumes called...");
  return await callApi("GET", `/profile/resumes/`);
};

const getResumeContent = async (resumeId) => {
  console.log(`[jobService] getResumeContent called for ID: ${resumeId}.`);
  const responseData = await callApi(
    "GET",
    `/profile/resumes/${resumeId}/content`,
    null,
    {
      headers: { Accept: "application/json, text/plain;q=0.8" },
      responseType: "text",
    }
  );

  if (typeof responseData === "string") {
    return responseData;
  } else if (responseData && typeof responseData.content === "string") {
    console.warn(
      "[jobService getResumeContent] API returned JSON with content key instead of direct text."
    );
    return responseData.content;
  } else {
    const errorDetail =
      responseData?.detail ||
      `Failed to retrieve content for resume ID ${resumeId}. Unexpected format.`;
    throw new Error(errorDetail);
  }
};

// --- NEW: Search jobs by specific category ---
const searchJobsByCategory = async (params) => {
  console.log("[jobService] searchJobsByCategory called with:", params);

  const { session_id, task_id, category_title, location, match_percentage } =
    params;

  if (!session_id || !category_title || !location) {
    throw new Error("session_id, category_title, and location are required");
  }

  const requestData = {
    session_id,
    task_id,
    category_title,
    location,
    match_percentage,
  };

  try {
    // Call the new backend endpoint
    const response = await callApi(
      "POST",
      `/jobs/search-by-category`,
      requestData
    );

    console.log("[jobService] Category search response:", response);

    return response;
  } catch (error) {
    console.error("[jobService] searchJobsByCategory error:", error);
    throw error;
  }
};

// --- DATA TRANSFORMATION LOGIC ---
export const transformJobData = (backendJobs) => {
  if (!Array.isArray(backendJobs)) {
    console.warn(
      "[transformJobData] Input is not an array, returning []. Received:",
      backendJobs
    );
    return [];
  }
  return backendJobs
    .map((job, index) => {
      if (typeof job !== "object" || job === null) return null;
      const fallbackId = `matched-job-${index}-${Date.now()}`;
      const extensions = job.detected_extensions || {};
      const matchPercentageRaw = job.match_percentage || job.matchScore;
      const matchPercentage =
        typeof matchPercentageRaw === "number"
          ? parseFloat(matchPercentageRaw.toFixed(2))
          : 0;
      const locationString = job.location || "";
      const scheduleType =
        extensions.schedule_type || job.employment_type || job.jobType || "";
      const isRemote =
        scheduleType.toLowerCase().includes("remote") ||
        locationString.toLowerCase().includes("remote");
      const remoteStatus = isRemote
        ? "Remote"
        : locationString
        ? "On-site/Hybrid"
        : "N/A";
      const applicantsCount = extensions.applications || job.applicant_count;
      const applicantsText =
        typeof applicantsCount === "number"
          ? `${applicantsCount} applicants`
          : "Apply Now";
      let salaryText =
        extensions.salary || job.salary || job.salary_range || job.pay || "";
      if (
        !salaryText ||
        salaryText.toLowerCase() === "competitive salary" ||
        salaryText.toLowerCase() === "n/a"
      )
        salaryText = "Competitive";
      let jobTypeText =
        extensions.schedule_type || job.employment_type || job.jobType || "";
      if (!jobTypeText) jobTypeText = "Full-time";
      let levelText = extensions.experience_level || job.level || "";
      if (!levelText) levelText = "Not Specified";

      // Job link detection - robust for SerpAPI
      const jobLink =
        job.apply_links?.[0]?.link || // Prioritize direct apply links
        job.link || // Check for a top-level link
        job.related_links?.[0]?.link || // Fallback to Google Jobs link
        job.url || // Generic URL fallback
        "#"; // Final fallback

      return {
        id: job.job_id || job.id || job.google_job_id || fallbackId,
        jobTitle: job.title || "N/A",
        companyName: job.company_name || job.company || "N/A",
        location: locationString || "N/A",
        description: job.description || "No description available.",
        link: jobLink,
        matchPercentage: matchPercentage,
        postedDate:
          extensions.posted_at || job.posted_date || job.datePosted || null,
        salary: salaryText,
        employmentType: jobTypeText,
        level: levelText,
        jobType: jobTypeText,
        remote: remoteStatus,
        applicants: applicantsText,
        company_description:
          job.company_details?.description ||
          extensions.company_description ||
          "",
        // Adding raw qualifications and skills for JobDetails to use
        qualifications:
          job.job_highlights?.Qualifications || job.qualifications || [],
        keySkills: job.job_highlights?.Skills || job.skills || [],
        thumbnail: job.thumbnail || null, // Add the thumbnail URL
      };
    })
    .filter((job) => job !== null);
};

// --- FETCH A SPECIFIC PAGE OF JOB RESULTS ---
const fetchJobResultsPage = async (taskId, page = 1, limit = 10) => {
  console.log(`[jobService] Fetching page ${page} for task ${taskId}`);
  // Your backend must support this paginated endpoint
  const response = await callApi(
    "GET",
    `/jobs/${taskId}/results?page=${page}&limit=${limit}`
  );

  // Expects a response like: { jobs: [...], pagination: { current_page: 1, total_pages: 5, ... } }
  if (response && Array.isArray(response.jobs) && response.pagination) {
    return {
      jobs: transformJobData(response.jobs),
      pagination: response.pagination,
    };
  }

  // Fallback if the API returns a flat array (no pagination)
  console.warn(
    "[jobService] fetchJobResultsPage received a non-paginated response. Simulating pagination."
  );
  if (Array.isArray(response)) {
    return {
      jobs: transformJobData(response),
      pagination: { current_page: 1, total_pages: 1 },
    };
  }

  throw new Error("Invalid response format for paginated jobs.");
};

// --- POLLING FUNCTION ---
const pollJobMatchResults = async (
  taskId,
  maxAttempts = 20,
  initialInterval = 2000,
  maxInterval = 10000
) => {
  let attempts = 0;
  let interval = initialInterval;

  const checkStatus = async () => {
    attempts++;
    try {
      // Step 1: Poll the original task status endpoint
      const response = await getJobMatchResults(taskId);

      if (response?.status === "COMPLETED") {
        // Step 2: Once complete, fetch ONLY THE FIRST PAGE of actual results
        console.log(
          `[jobService] Task ${taskId} completed. Fetching initial results.`
        );
        return await fetchJobResultsPage(taskId, 1, 10);
      } else if (response?.status === "FAILED") {
        let errorMessage = "Job matching process failed.";
        if (response.result) {
          try {
            const errorInfo = JSON.parse(response.result);
            errorMessage = errorInfo?.error || errorMessage;
          } catch {
            errorMessage =
              typeof response.result === "string" &&
              response.result.length < 200
                ? response.result
                : errorMessage;
          }
        }
        throw new Error(errorMessage);
      } else if (
        ["PENDING", "PROCESSING", "STARTED", "UNKNOWN"].includes(
          response.status
        )
      ) {
        if (
          response.status === "UNKNOWN" &&
          response.message === "Task not found."
        ) {
          throw new Error(`Job matching task ${taskId} not found.`);
        }
        if (attempts < maxAttempts) {
          return new Promise((resolve) => {
            interval = Math.min(interval * 1.5, maxInterval);
            setTimeout(() => resolve(checkStatus()), interval);
          });
        } else {
          throw new Error(
            `Timeout waiting for job match results for task ${taskId}.`
          );
        }
      } else {
        throw new Error(
          `Job matching task ended with unexpected status: ${
            response?.status || "Unknown"
          }`
        );
      }
    } catch (error) {
      if (attempts >= maxAttempts) {
        throw new Error(
          `Polling failed definitively: ${
            error.message || "Unknown polling error."
          }`
        );
      }
      throw error;
    }
  };
  return await checkStatus();
};

// --- Combined Actions ---
const saveAndMatchResume = async (
  file,
  email,
  location,
  title = null,
  setAsPrimary = true
) => {
  const savedResume = await saveResumeFile(file, title, setAsPrimary);
  if (!savedResume || !savedResume.id) {
    throw new Error("Failed to save resume or get ID from saved resume.");
  }
  let resumeContent;
  let resumeIdToFetchContent = savedResume.id;
  try {
    if (setAsPrimary) {
      const documents = await getUserDocuments();
      const primaryResume = documents.find(
        (doc) => doc.is_primary && doc.document_type === "resume"
      );
      if (primaryResume) {
        resumeIdToFetchContent = primaryResume.id;
      } else {
        console.warn(
          `No primary resume found after update. Using recently uploaded resume ID: ${savedResume.id}`
        );
        resumeIdToFetchContent = savedResume.id;
      }
    }
    resumeContent = await getResumeContent(resumeIdToFetchContent);
    if (typeof resumeContent !== "string" || resumeContent.trim().length < 50) {
      throw new Error(
        "Retrieved resume content is invalid or too short for matching."
      );
    }
  } catch (contentError) {
    throw new Error(
      `Failed to retrieve resume content for matching: ${contentError.message}`
    );
  }
  return await submitResumeForMatching(email, resumeContent, location);
};

const matchWithSavedResume = async (resumeId, email, location) => {
  let resumeContent;
  try {
    resumeContent = await getResumeContent(resumeId);
    if (typeof resumeContent !== "string" || resumeContent.trim().length < 50)
      throw new Error("Retrieved content invalid/short.");
  } catch (contentError) {
    throw new Error(
      `Failed retrieve content for ID ${resumeId}: ${contentError.message}`
    );
  }
  return await submitResumeForMatching(email, resumeContent, location);
};

// New function to match using only profile (no resume)
const matchWithProfile = async (email, location) => {
  console.log("[jobService] matchWithProfile - profile-based job matching");
  return await submitProfileForMatching(email, location);
};

// --- Default Export: The Service Object ---
const jobService = {
  // Core functions
  submitResumeForMatching, // Updated to handle both resume and profile
  submitProfileForMatching, // Explicit profile-based matching
  saveResumeFile,
  uploadResumeFile,
  getSavedResumes,
  getResumeContent,

  // Job data handling
  transformJobData,
  fetchJobResultsPage,
  pollJobMatchResults,
  getJobMatchResults,

  // Combined actions
  saveAndMatchResume,
  matchWithSavedResume,
  matchWithProfile, // New profile-based matching

  // NEW: Category-specific search
  searchJobsByCategory,
};

export default jobService;
